import React from 'react'
import { Factory, BarChart3, FileText, Settings2, ClipboardList, X, Search, SlidersHorizontal } from 'lucide-react'

interface SidebarProps {
  activeModule: string
  onModuleChange: (module: string) => void
  showFilters: boolean
  onFilterChange: (filters: any) => void
  availableHalls?: string[]
  isOpen?: boolean
  onClose?: () => void
}

const modules = [
  { id: 'minimap',  label: 'Machinepark',        icon: Factory },
  { id: 'tasks',    label: 'Takenbeheer',        icon: ClipboardList },
  { id: 'analytics',label: 'Kwaliteit Analytics',icon: BarChart3 },
  { id: 'reports',  label: 'Rapporten & Export', icon: FileText },
  { id: 'machines', label: 'Machine Beheer',     icon: Settings2 },
]

export const Sidebar: React.FC<SidebarProps> = ({
  activeModule,
  onModuleChange,
  showFilters,
  onFilterChange,
  availableHalls = [],
  isOpen = false,
  onClose,
}) => {
  const handleSelect = (id: string) => {
    onModuleChange(id)
    onClose?.()
  }

  const content = (
    <div className="flex flex-col h-full p-3 sm:p-4 gap-1">
      {/* Mobile header */}
      <div className="flex items-center justify-between mb-3 md:hidden">
        <span className="section-label">Navigatie</span>
        <button
          onClick={onClose}
          className="p-2 rounded-xl hover:bg-[var(--kvh-red-alpha)] transition-colors touch-target flex items-center justify-center"
        >
          <X size={18} style={{ color: 'var(--text-secondary)' }} />
        </button>
      </div>

      {/* Section label desktop */}
      <div className="mb-3 hidden md:block">
        <p className="section-label">Werkruimte</p>
        <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>Beheer en analyse</p>
      </div>

      {/* Nav items */}
      <nav className="flex flex-col gap-0.5">
        {modules.map(({ id, label, icon: Icon }) => {
          const active = activeModule === id
          return (
            <button
              key={id}
              onClick={() => handleSelect(id)}
              className={`nav-item ${active ? 'active' : ''}`}
            >
              <Icon size={16} className="flex-shrink-0" />
              <span>{label}</span>
              {active && <span className="ml-auto text-[10px] font-bold tracking-wide">ACTIEF</span>}
            </button>
          )
        })}
      </nav>

      {/* Filters (alleen minimap) */}
      {showFilters && (
        <>
          <div className="mt-6 mb-2 pt-5 border-t" style={{ borderColor: 'var(--border)' }}>
            <p className="section-label flex items-center gap-2"><SlidersHorizontal size={12} /> Filters</p>
          </div>

          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-tertiary)' }} />
              <input
                type="text"
                placeholder="Zoek machine…"
                className="input-field text-sm !pl-9"
                onChange={e => onFilterChange({ search: e.target.value })}
              />
            </div>

            <div>
              <label className="section-label block mb-1.5">Hal / Locatie</label>
              <select
                className="input-field text-sm"
                onChange={e => onFilterChange({ selectedHal: e.target.value || undefined })}
              >
                <option value="">Alle hallen</option>
                {availableHalls.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>

            <div>
              <label className="section-label block mb-1.5">Status</label>
              <select
                className="input-field text-sm"
                onChange={e => onFilterChange({ selectedStatus: e.target.value || undefined })}
              >
                <option value="">Alle statussen</option>
                <option value="active">Actief</option>
                <option value="inactive">Inactief</option>
                <option value="maintenance">Onderhoud</option>
              </select>
            </div>

            <label className="flex items-center gap-2.5 text-sm cursor-pointer touch-target" style={{ color: 'var(--text-primary)' }}>
              <input
                type="checkbox"
                className="w-4 h-4 rounded accent-kvh-red"
                onChange={e => onFilterChange({ showProblems: e.target.checked })}
              />
              Alleen met problemen
            </label>

            <label className="flex items-center gap-2.5 text-sm cursor-pointer touch-target" style={{ color: 'var(--text-primary)' }}>
              <input
                type="checkbox"
                className="w-4 h-4 rounded"
                style={{ accentColor: '#f59e0b' }}
                onChange={e => onFilterChange({ showTasks: e.target.checked })}
              />
              Te weinig inspecties
            </label>
          </div>
        </>
      )}
    </div>
  )

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
          onClick={onClose}
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-[280px]
          md:hidden overflow-y-auto
          transition-transform duration-300 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ background: 'var(--card)', borderRight: '1px solid var(--border)' }}
      >
        {content}
      </aside>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-[248px] shrink-0 sidebar-surface overflow-hidden">
        {content}
      </aside>
    </>
  )
}
